import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useSFConnectionState } from '@contexts/sf-connection-context';

import { ConnectionSchema } from '@modules/salesforce/shared/data/connection-schema';

import {
  SalesforceConnection,
  SalesforceConnectionEdition,
  SalesforceLoginMethod,
} from '@modules/salesforce/models';

import { getConnectionEnv } from '@modules/salesforce/utils/utils';

export const useCreateConnectionForm = () => {
  const { selectedConnection } = useSFConnectionState();

  const getDefaultValues = (connection: SalesforceConnection) => {
    const defaultValues = {
      ...connection,
      name: connection?.name,
      salesforceConnectionEdition: getConnectionEnv(connection),
      loginMethod: connection?.loginMethod ?? SalesforceLoginMethod.OAUTH,
      isAdmin: connection?.isAdmin ?? false,
      username: connection?.username,
      password: connection?.password,
      securityToken: connection?.securityToken,
      clientId: connection?.clientId,
      clientSecret: connection?.clientSecret,
      callbackUrl: connection?.callbackUrl,
      accessToken: connection?.accessToken,
      refreshToken: connection?.refreshToken,
      instanceUrl: connection?.instanceUrl,
      expirationDate: connection?.expirationDate,
    };

    return defaultValues;
  };

  const {
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(ConnectionSchema),
    defaultValues: getDefaultValues(selectedConnection),
  });

  const handleUpdateFormValue = (fieldId: any, fieldValue: any) => {
    if (fieldId === 'loginMethod' && fieldValue !== watch('loginMethod')) {
      setValue('callbackUrl', null);
      setValue('accessToken', null);
      setValue('refreshToken', null);
      setValue('password', null);
      setValue('securityToken', null);
    }

    // Clear scratch org fields when switching away from scratch org
    if (
      fieldId === 'salesforceConnectionEdition' &&
      fieldValue !== SalesforceConnectionEdition.SCRATCH_ORG
    ) {
      setValue('instanceUrl', null);
      setValue('expirationDate', null);
    }

    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return {
    isValid,
    loginMethod: watch('loginMethod') as SalesforceLoginMethod,
    handleSubmit,
    values: getValues(),
    handleUpdateFormValue,
    watch,
  };
};
